/**
 * Loading Progress Component
 * Displays progress during node geometry loading
 */

import React from 'react';
import type { FC } from 'react';
import styled from 'styled-components';
import type { LoadingProgressProps } from '../types';

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: ${props => props.theme?.panelBackground || '#0d1117'};
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme?.textColor || '#e6edf3'};
`;

const CancelButton = styled.button`
  padding: 0.375rem 0.75rem;
  background: transparent;
  color: ${props => props.theme?.errorColor || '#f85149'};
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.theme?.errorColor || '#f85149'}22;
    border-color: ${props => props.theme?.errorColor || '#f85149'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PhaseIndicator = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme?.labelColor || '#8b949e'};
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: ${props => props.theme?.inputBgd || '#161b22'};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBar = styled.div.withConfig({
  shouldForwardProp: prop => !['progress', 'success', 'error'].includes(prop as string),
})<{ progress: number; success?: boolean; error?: boolean }>`
  height: 100%;
  width: ${props => props.progress}%;
  background: ${props => {
    if (props.error) return props.theme?.errorColor || '#f85149';
    if (props.success) return props.theme?.successColor || '#3fb950';
    return props.theme?.primaryColor || '#58a6ff';
  }};
  transition:
    width 0.3s ease,
    background 0.3s ease;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  font-size: 0.75rem;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StatLabel = styled.div`
  color: ${props => props.theme?.labelColor || '#8b949e'};
`;

const StatValue = styled.div.withConfig({
  shouldForwardProp: prop => (prop as string) !== 'error',
})<{ error?: boolean }>`
  color: ${props =>
    props.error ? props.theme?.errorColor || '#f85149' : props.theme?.textColor || '#e6edf3'};
  font-weight: 500;
`;

const ErrorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 100px;
  overflow-y: auto;
  padding: 0.5rem;
  background: ${props => props.theme?.errorColor || '#f85149'}11;
  border: 1px solid ${props => props.theme?.errorColor || '#f85149'}33;
  border-radius: 4px;
`;

const ErrorItem = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme?.errorColor || '#f85149'};
`;

// ============================================================================
// Component
// ============================================================================

export const LoadingProgress: FC<LoadingProgressProps> = ({ progress, onCancel }) => {
  if (!progress) {
    return null;
  }

  const totalNodes = progress.total;
  const completedNodes = progress.completed;
  const failedNodes = progress.failed;
  const progressPercent = totalNodes > 0 ? (completedNodes / totalNodes) * 100 : 0;
  const isComplete = completedNodes + failedNodes >= totalNodes;
  const hasErrors = failedNodes > 0;

  const getPhaseText = (): string => {
    if (isComplete) {
      if (hasErrors) {
        return `Complete with ${failedNodes} error${failedNodes !== 1 ? 's' : ''}`;
      }
      return 'Complete';
    }
    return `Loading node geometries...`;
  };

  return (
    <Container>
      <Header>
        <Title>Loading Progress</Title>
        {!isComplete && onCancel && (
          <CancelButton onClick={onCancel} aria-label='Cancel loading'>
            Cancel
          </CancelButton>
        )}
      </Header>

      <PhaseIndicator>{getPhaseText()}</PhaseIndicator>

      <ProgressBarContainer>
        <ProgressBar
          progress={progressPercent}
          success={isComplete && !hasErrors}
          error={hasErrors}
          role='progressbar'
          aria-valuenow={progressPercent}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </ProgressBarContainer>

      <Stats>
        <Stat>
          <StatLabel>Total</StatLabel>
          <StatValue>{totalNodes}</StatValue>
        </Stat>
        <Stat>
          <StatLabel>Completed</StatLabel>
          <StatValue>{completedNodes}</StatValue>
        </Stat>
        <Stat>
          <StatLabel>Failed</StatLabel>
          <StatValue error={failedNodes > 0}>{failedNodes}</StatValue>
        </Stat>
      </Stats>

      {hasErrors && progress.errors && progress.errors.length > 0 && (
        <ErrorList>
          {progress.errors.slice(0, 5).map((error, index) => (
            <ErrorItem key={index}>
              {error.lsid}: {error.error}
            </ErrorItem>
          ))}
          {progress.errors.length > 5 && (
            <ErrorItem>
              ... and {progress.errors.length - 5} more error
              {progress.errors.length - 5 !== 1 ? 's' : ''}
            </ErrorItem>
          )}
        </ErrorList>
      )}
    </Container>
  );
};
